/** A bounded, supported roof vocabulary for the first authored program.
 * Shares the frontier kit's timber decks, overlapping tile rows and ridge
 * construction through the existing procedural-asset format. */
import type {AssetNode,AssetVec3,ProceduralAssetV1} from '../proceduralAssets.js';
import type {BuildingPlan,LocalBox} from './plan.js';
import {rngFromString} from '../rng.js';
import * as pmath from '../pmath.js';
export interface HeritageRoof {
  id:string;kind:'gable';version:1|2;parts?:{id:string;role:'hall'|'wing';bounds:HeritageRoof['bounds']}[];bounds:{x:number;z:number;width:number;depth:number;eave:number;rise:number};
  asset:ProceduralAssetV1;collision:LocalBox[];
}
export function heritageRoof(plan:BuildingPlan,palette:string,pitch?:number):HeritageRoof {
  if(!plan.shell||plan.levels!==1)throw new Error('The terracotta roof requires a one-storey walk-in shell.');
  const cells=plan.shell.floors,u=plan.unit,minX=Math.min(...cells.map(c=>c.x))-u/2,maxX=Math.max(...cells.map(c=>c.x))+u/2,minZ=Math.min(...cells.map(c=>c.z))-u/2,maxZ=Math.max(...cells.map(c=>c.z))+u/2;
  const width=maxX-minX,depth=maxZ-minZ,x=(minX+maxX)/2,z=(minZ+maxZ)/2;
  if(width>20||depth>20||Math.abs(cells.length*u*u-width*depth)>.01)throw new Error('The terracotta roof requires a rectangular footprint; annex joins need their own roof plan.');
  const form=rngFromString(`heritage-roof:form:1:${plan.seed}`),detail=rngFromString(`heritage-roof:tiles:1:${plan.seed}`);
  const angle=pitch??[.42,.48,.54][Math.floor(form()*3)],slope=pmath.sin(angle)/pmath.cos(angle),eave=4.05,overhang=.45,half=depth/2+overhang,peak=eave+depth/2*slope,w=width+overhang*2;
  const nodes:AssetNode[]=[],collision:LocalBox[]=[];
  const box=(id:string,size:AssetVec3,p:AssetVec3,material:number,r:AssetVec3=[0,0,0])=>{const n:AssetNode={id,kind:'box',params:size,p:[x+p[0],p[1],z+p[2]],r,material};nodes.push(n);return n;};
  for(const side of [-1,1]){
    box(`deck:${side}`,[w,.16,half/pmath.cos(angle)],[0,peak-half/2*slope,side*half/2],1,[side*angle,0,0]);
    const rows=Math.ceil(half/.62),columns=Math.ceil(w/.7);
    for(let row=0;row<rows;row++){
      const zz=(row+.5)*half/rows;
      // Interleave four independently colored tile runs. A color per whole
      // row produces stripes; per-tile nodes would exceed the asset budget.
      for(let lane=0;lane<Math.min(4,columns);lane++){
        const count=Math.floor((columns-1-lane)/4)+1;
        const tile=box(`tiles:${side}:${row}:${lane}`,[w/columns-.025,.095,half/rows/pmath.cos(angle)+.13],[-w/2+(lane+.5)*w/columns,peak-zz*slope+.13+(rows-row)*.009,side*zz],detail()<.5?2:3,[side*angle,0,0]);
        if(count>1)tile.modifiers=[{type:'array',mode:'linear',count,offset:[4*w/columns,0,0]}];
      }
    }
    box(`eaves:${side}`,[w+.12,.19,.2],[0,peak-half*slope,side*half],1);
    // Thin tapered gable infill: one supported panel per end, embedded into
    // the shell and timber deck. Taper is a kit-supported vertex operation.
    const gable=box(`gable:${side}`,[.42,peak-3.96,depth],[side*(width/2-.2),(peak+3.96)/2,0],0);
    gable.modifiers=[{type:'taper',axis:'y',factor:0}];
    for(const roofSide of [-1,1])box(`barge:${side}:${roofSide}`,[.18,.2,half/pmath.cos(angle)],[side*(w/2-.06),peak-half/2*slope,roofSide*half/2],1,[roofSide*angle,0,0]);
  }
  for(const sx of [-1,1])for(const sz of [-1,1])box(`corner-post:${sx}:${sz}`,[.2,4.1,.2],[sx*(width/2-.08),2.05,sz*(depth/2-.08)],1);
  box('ridge',[w+.15,.22,.24],[0,peak+.22,0],3);
  // Solid non-enterable attic. Thin depth strips bound the sloping surface
  // within 0.18 m vertically; the existing room ceiling remains authoritative.
  const strips=Math.ceil(depth/.45),step=depth/strips;
  for(let i=0;i<strips;i++){
    const zz=-depth/2+(i+.5)*step,top=peak-(Math.abs(zz)+step/2)*slope+.1;
    collision.push({x,y:4,z:z+zz,w:width,h:Math.max(.05,top-4),d:step});
  }
  const asset:ProceduralAssetV1={version:1,id:'heritage-gable-v1',name:'Supported terracotta roof',kind:'buildingModule',tags:['heritage','roof'],buildingModule:{footprintUnits:[width/u,depth/u]},materials:[
    {color:palette==='ochre'?0xc9a570:0xe5d9b9,roughness:1,metalness:0},
    {color:0x614631,roughness:1,metalness:0},
    {color:0xaf5836,roughness:1,metalness:0},
    {color:0xbd6540,roughness:1,metalness:0},
  ],nodes};
  return {id:'roof:main',kind:'gable',version:1,bounds:{x,z,width,depth,eave,rise:peak-eave},asset,collision};
}

/** Resolve an L or C footprint into one dominant hall and lower, abutting
 * wings. Roofs meet the hall's gable wall; no unsupported valley solver. */
export function composedHeritageRoof(plan:BuildingPlan,palette:string):HeritageRoof {
  if(!plan.shell||plan.levels!==1)throw new Error('Composed roofs require a one-storey shell');
  const cells=plan.shell.floors,u=plan.unit,zs=[...new Set(cells.map(c=>c.z))].sort((a,b)=>a-b);
  const fullX=[...new Set(cells.map(c=>c.x))].filter(x=>zs.every(z=>cells.some(c=>c.x===x&&c.z===z)));
  if(cells.every(c=>fullX.includes(c.x)))return heritageRoof(plan,palette);
  const hall=cells.filter(c=>fullX.includes(c.x)),rest=cells.filter(c=>!fullX.includes(c.x));
  if(!hall.length)throw new Error('No continuous hall supports this roof composition');
  const groups=[hall];let previousZ=-Infinity;
  for(const z of zs){
    const row=rest.filter(c=>c.z===z);if(!row.length)continue;
    if(z-previousZ>u+.01)groups.push([]);
    groups.at(-1)!.push(...row);previousZ=z;
  }
  const resolved=groups.map((floors,i)=>heritageRoof({...plan,seed:(plan.seed+i*2654435761)>>>0,shell:{...plan.shell!,floors}},palette,i===0?.54:.32));
  const minX=Math.min(...cells.map(c=>c.x))-u/2,maxX=Math.max(...cells.map(c=>c.x))+u/2,minZ=zs[0]-u/2,maxZ=zs.at(-1)!+u/2;
  const nodes:AssetNode[]=[],parts:NonNullable<HeritageRoof['parts']>=[];
  for(const [i,roof] of resolved.entries()){
    const id=i===0?'hall':`wing-${i}`,bounds=roof.bounds;
    parts.push({id,role:i===0?'hall':'wing',bounds});
    for(const source of roof.asset.nodes){
      // Full-height posts on a rectangle's internal corners would stand in
      // the connecting room. Shell facade framing remains the wall's job.
      if(source.id.startsWith('corner-post:'))continue;
      const n=structuredCloneNode(source);n.id=`${id}:${n.id}`;
      if(i>0&&!n.id.includes('gable:')){
        // Bury the wing end only 6 cm into the supporting gable. The far end
        // retains its overhang. Avoid two coplanar roof sheets at the join.
        const hallB=resolved[0].bounds,right=bounds.x>hallB.x,oldWidth=bounds.width+.9,newWidth=bounds.width+.51,scale=newWidth/oldWidth;
        n.p[0]=bounds.x+(n.p[0]-bounds.x)*scale+(right?.195:-.195);
        if(n.params)n.params[0]*=scale;
        for(const m of n.modifiers??[])if(m.type==='array'&&m.mode==='linear')m.offset[0]*=scale;
      }
      nodes.push(n);
    }
  }
  const first=resolved[0];
  return {id:'roof:composition',kind:'gable',version:2,parts,
    bounds:{x:(minX+maxX)/2,z:(minZ+maxZ)/2,width:maxX-minX,depth:maxZ-minZ,eave:4.05,rise:first.bounds.rise},
    asset:{...first.asset,id:'heritage-composed-v1',name:'Hall with attached wings',buildingModule:{footprintUnits:[(maxX-minX)/u,(maxZ-minZ)/u]},nodes},
    collision:resolved.flatMap(r=>r.collision)};
}
function structuredCloneNode(n:AssetNode):AssetNode {
  // Explicit data copy keeps the shared core portable to workers and Node.
  return JSON.parse(JSON.stringify(n)) as AssetNode;
}
